import { useContext } from 'react'
import PropTypes from 'prop-types'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject, deepSetObject } from '@domain-group/javascript-utils'

import useProvider, { useProviderForm } from '@hooks/useProvider'

import ToggleListItem from '@components/ToggleListItem'

const toggleOption = (providerFormPayload, key) => {
  const { entityState: { options }, setEntityState } = providerFormPayload

  const existingValue = options[key]
  const updatedValue = typeof existingValue === 'string' ? (existingValue === 'false').toString() : !existingValue

  const updatedOptions = deepSetObject(options, key, updatedValue)

  setEntityState({ options: updatedOptions })
}

const handleImageQualityChange = (value, providerFormPayload) => {
  const { entityState: { options }, setEntityState } = providerFormPayload

  const existingValue = options?.image_quality
  const updatedValue = Number.isNaN(+value) ? existingValue : value

  const updatedOptions = deepSetObject(options, 'image_quality', updatedValue)
  setEntityState({ options: updatedOptions })
}

const ManageProviderOrderDeliveryModal = (props) => {
  const { callbacks, modalKey, showModal } = props
  const { closeModal, updateProvider } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { provider: initProvider } = modalPayload

  const providerPayload = useProvider(initProvider)
  const {
    provider,
    callbacks: {
      updateProvider: updateFn,
    },
    updating,
  } = providerPayload

  const providerFormPayload = useProviderForm(provider)
  const {
    entityState,
    entityState: {
      image_mode,
      options: {
        downsample_color_images,
        image_icc_profile,
        image_icc_profile_rgb,
        image_quality,
        optimize_files,
        outline_pdf_fonts,
        pdf_compatibility,
        strip_image_profile,
      },
    },
    handlers,
  } = providerFormPayload

  const updateProviderPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'File and Order Deliver Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header
        callbacks={callbacks}
        title="File &amp;"
        titleSecondLine="Order Delivery"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="PDF Version">
            <select
              name="options.pdf_compatibility"
              type="text"
              value={pdf_compatibility}
              {...handlers}
            >
              <option value="">Default</option>
              <option value="1.3">1.3</option>
              <option value="1.4">1.4</option>
              <option value="1.5">1.5</option>
              <option value="1.6">1.6</option>
              <option value="1.7">1.7 (Default)</option>
            </select>
          </Form.Field>

          <Form.Field label="Image Mode" marginTop="large">
            <select
              min="0"
              name="image_mode"
              type="text"
              value={image_mode}
              {...handlers}
            >
              <option value="rgb">RGB</option>
              <option value="cmyk">CMYK</option>
            </select>
          </Form.Field>

          <Form.Field label="Image Quality (1-100)" marginTop="large">
            <input
              onChange={e => handleImageQualityChange(e.target.value, providerFormPayload)}
              type="text"
              value={image_quality}
            />
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'large' }}>Image Profiles</SectionDivider>

          <ToggleListItem
            boxProps={{ marginBottom: 0 }}
            onClick={() => toggleOption(providerFormPayload, 'strip_image_profile')}
            toggleOffText="No"
            toggleOnText="Yes"
            toggleValue={strip_image_profile === 'true'}
          >
            <ToggleListItem.Title>Strip Image Profiles</ToggleListItem.Title>
            <ToggleListItem.Description>
              If yes, don&apos;t show image profile settings.
            </ToggleListItem.Description>
          </ToggleListItem>

          <Form.Field label="CMYK Profile" marginTop="large">
            <select
              name="options.image_icc_profile"
              type="text"
              value={image_icc_profile}
              {...handlers}
            >
              <option value="">Don&apos;t Convert (Default)</option>
              <option value="CoatedFOGRA27.icc">Coated FOGRA27</option>
              <option value="CoatedGRACoL2006.icc">Coated GRACoL2006</option>
              <option value="Printforce_CustomCMYK.icc">Printforce Custom CMYK</option>
            </select>
          </Form.Field>

          <Form.Field label="RGB Profile" marginTop="large">
            <select
              name="options.image_icc_profile_rgb"
              type="text"
              value={image_icc_profile_rgb}
              {...handlers}
            >
              <option value="">Don&apos;t Convert (Default)</option>
              <option value="AdobeRGB1998.icc">Adobe RGB</option>
            </select>
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'large' }}>File Optimisation</SectionDivider>

          <ToggleListItem
            boxProps={{ marginBottom: 0 }}
            onClick={() => toggleOption(providerFormPayload, 'optimize_files')}
            toggleOffText="No"
            toggleOnText="Yes"
            toggleValue={optimize_files}
          >
            <ToggleListItem.Title>Optimise Files</ToggleListItem.Title>
            <ToggleListItem.Description>
              Reduces file size (MB) and allows Outline Font & Downsample options to be enabled.
            </ToggleListItem.Description>
          </ToggleListItem>

          <ToggleListItem
            boxProps={{ marginBottom: 0, marginTop: 'large' }}
            onClick={() => toggleOption(providerFormPayload, 'outline_pdf_fonts')}
            toggleOffText="No"
            toggleOnText="Yes"
            toggleValue={outline_pdf_fonts}
          >
            <ToggleListItem.Title>Outline Fonts</ToggleListItem.Title>
            <ToggleListItem.Description>
              Fonts will be outlined on delivery of artworks to provider. Optimise Files must be enabled.
            </ToggleListItem.Description>
          </ToggleListItem>

          <ToggleListItem
            boxProps={{ marginBottom: 0, marginTop: 'large' }}
            onClick={() => toggleOption(providerFormPayload, 'downsample_color_images')}
            toggleOffText="No"
            toggleOnText="Yes"
            toggleValue={downsample_color_images}
          >
            <ToggleListItem.Title>Downsample Images</ToggleListItem.Title>
            <ToggleListItem.Description>
              Reduces size of images within output files. Optimise Files must be enabled.
            </ToggleListItem.Description>
          </ToggleListItem>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateProvider(updateProviderPayload)}
          size="large"
        >
          Save File &amp; Order Delivery
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

ManageProviderOrderDeliveryModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

ManageProviderOrderDeliveryModal.defaultProps = {
  modalKey: 'ManageProviderOrderDeliveryModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <ManageProviderOrderDeliveryModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
